import {
  addDays,
  differenceInCalendarDays,
  formatISO,
  parseISO,
} from 'date-fns'
import { type Draft } from 'immer'
import { type MonthCalendarResponse } from '../types'
import { moveContent } from './moveContent'

export type MomentMove = {
  /** `yyyy-MM-dd`. */
  startDate: string
  /** `yyyy-MM-dd`, or null for a moment that lasts a single day. */
  endDate: string | null
}

const shiftDay = (day: string, days: number): string =>
  formatISO(addDays(parseISO(day), days), { representation: 'date' })

const shiftInstant = (instant: string, days: number): string =>
  addDays(parseISO(instant), days).toISOString()

/**
 * Puts a moment on new dates, and carries its content with it.
 *
 * Content is stored as an offset from its moment's start, so the server moves
 * every piece of content whenever the moment moves. The cache has to say the
 * same thing, or the chips sit on the old days until the window is fetched
 * again.
 *
 * A moment that is not in the window is left alone: a person can drag one
 * month's moment while another month sits in the cache, and that month has
 * nothing to say about it.
 */
export const moveMoment =
  (momentId: string, move: MomentMove) =>
  (draft: Draft<MonthCalendarResponse>) => {
    const moment = draft.data.moments.by_id[momentId]
    if (moment === undefined) return draft

    const days =
      moment.startDate === null
        ? 0
        : differenceInCalendarDays(
            parseISO(move.startDate),
            parseISO(moment.startDate)
          )

    moment.startDate = move.startDate
    moment.endDate = move.endDate

    if (days === 0) return draft

    Object.values(draft.data.content.by_id).forEach((content) => {
      if (content.momentId !== momentId) return
      if (content.scheduledDate === null) return

      moveContent(content.id, {
        scheduledAt:
          content.scheduledAt === null
            ? null
            : shiftInstant(content.scheduledAt, days),
        scheduledDate: shiftDay(content.scheduledDate, days),
        takedownDate:
          content.takedownDate === null
            ? null
            : shiftDay(content.takedownDate, days),
      })(draft)
    })

    return draft
  }
